<template>
    <pendo-drawer
        :visible="visible"
        :title="title"
        :size="forceFullscreen ? '100%' : '600px'"
        :append-to="forceFullscreen ? undefined : appendTo"
        :mask="showMask"
        :placement="forceFullscreen ? undefined : placement"
        :position-strategy="forceFullscreen ? undefined : positionStrategy"
        class-name="multiselect-blade"
        :close-on-mask-click="closeOnMaskClick"
        :show-close="showCloseButton"
        :z-index="503"
        @open="$emit('open')"
        @opened="bladeOpen = true"
        @close="onClose"
        @closed="onClosed">
        <div
            v-if="loading"
            class="multiselect-blade__loading-state">
            <pendo-loading-indicator
                size="large"
                :text="$t('Loading...')" />
        </div>
        <div
            v-if="!loading"
            class="multiselect-blade__body">
            <div class="multiselect-blade__options-list-controls">
                <pendo-button
                    v-if="forceFullscreen"
                    theme="app"
                    type="link"
                    class="multiselect-blade__toggle-filters"
                    :label="showFiltersAndSearch ? $t('multiselectBlade.Hide_filters') : $t('multiselectBlade.Show_filters')"
                    :suffix-icon="showFiltersAndSearch ? 'chevron-up' : 'chevron-down'"
                    :aria-expanded="showFiltersAndSearch"
                    aria-controls="multiselect-blade-filters-region"
                    @click="showFiltersAndSearch = !showFiltersAndSearch" />
                <div
                    v-show="!forceFullscreen || showFiltersAndSearch"
                    id="multiselect-blade-filters-region">
                    <div class="multiselect-blade__activity-type-filter">
                        <slot name="activityTypeFilter" />
                    </div>
                    <search
                        v-model="search"
                        class="multiselect-blade__search"
                        full-width
                        :aria-label="$t('multiselectBlade.Search for {label}', { label: lowerCaseLabel })"
                        :placeholder="searchPlaceholder" />
                    <div class="multiselect-blade__selection-filters">
                        <slot name="filters" />
                    </div>
                </div>
                <div class="multiselect-blade__extra-info">
                    <slot name="extraInfo" />
                </div>
                <div
                    ref="multiselectBladeSelectionControls"
                    class="multiselect-blade__selection-controls"
                    :class="{ 'multiselect-blade__selection-controls--filters-visible': !forceFullscreen || showFiltersAndSearch }">
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        v-if="!isSingleSelectBlade"
                        class="selection-count"
                        aria-live="polite"
                        aria-atomic="true"
                        v-html="selectionCountLabel" />
                    <!-- eslint-enable vue/no-v-html -->
                    <div
                        v-if="isSingleSelectBlade"
                        class="single-selection">
                        <strong>{{ $t('multiselectBlade.Selected:') }}&nbsp;</strong>
                        <span v-if="!selectedValue.length">{{ $t('multiselectBlade.Choose an item below') }}</span>
                        <multiselect-blade-selected-item
                            v-for="val in selectedValue"
                            :key="val.id"
                            :icon="
                                allOptionsMap[val.id].icon ||
                                    allOptionsMap[val.id].platform ||
                                    allOptionsMap[val.id].kind
                            "
                            :app-icon="allOptionsMap[val.id].appIcon"
                            :label="allOptionsMap[val.id].name"
                            :sublabel="createOptionSublabel(allOptionsMap[val.id])" />
                    </div>
                    <pendo-button
                        v-if="!isSingleSelectBlade"
                        class="clear-selection-button"
                        theme="app"
                        type="subtle"
                        :disabled="!hasAnySelection"
                        :label="$t('multiselectBlade.Clear selection')"
                        @click="clearSelections" />
                    <pendo-button
                        v-if="showSelectAll && !isSingleSelectBlade"
                        v-pendo-tooltip="hasAllSelected && disabledTooltip"
                        theme="app"
                        type="secondary"
                        :disabled="hasAllSelected"
                        :label="$t('multiselectBlade.Select all')"
                        @click="selectAll" />
                </div>
                <div class="multiselect-blade__notification">
                    <slot name="notification" />
                </div>
            </div>
            <div
                v-if="showEmptyState"
                class="multiselect-blade__empty-state">
                <p class="header">
                    {{ emptyStateStrings.header }}
                </p>
                <p>{{ emptyStateStrings.noSearchResultsMsg }}</p>
                <p>{{ emptyStateStrings.suggestion }}</p>
            </div>
            <recycle-scroller
                role="listbox"
                :aria-label="$t('multiselectBlade.Select options')"
                class="multiselect-blade__options-list"
                :items="listData"
                :buffer="bufferHeight"
                :aria-multiselectable="max > 1"
                @scroll="onScroll">
                <template #before>
                    <fieldset
                        v-for="section in beforeSections"
                        :key="section.id"
                        class="multiselect-blade__fieldset">
                        <legend
                            :id="beforeSectionDescriptionId(section)"
                            class="sr-only">
                            {{ section.label }}
                        </legend>
                        <multiselect-blade-option
                            v-for="(row, rowIndex) in section.rows"
                            :key="getSectionRowKey(section, row, rowIndex)"
                            :is-single-select="isSingleSelectBlade"
                            :icon="row.kind || row.icon || row.platform"
                            :training-attributes="row.trainingAttributes"
                            :application-flags="row.applicationFlags"
                            :type="row.type"
                            :group-id="row.groupId"
                            :item-id="row.id"
                            :label="row.name"
                            :sublabel="createOptionSublabel(row)"
                            :color="row.color"
                            :before-options-tooltip="sectionTooltip(row)"
                            :indeterminate="isOptionIndeterminate(row)"
                            :value="isOptionSelected(row)"
                            :disabled="isOptionDisabled(row)"
                            :disabled-tooltip="disabledTooltip"
                            :collapsed="collapsedData.groups[row.id]"
                            :is-starred="row.isStarred"
                            :group-description-id="beforeSectionDescriptionId(section)"
                            @change="onOptionChange($event, row)"
                            @group-collapse="onGroupCollapse($event, row)"
                            @mouseenter="onOptionMouseenter($event, row)"
                            @mouseleave="$emit('mouseleave', $event)" />
                    </fieldset>
                </template>
                <template #default="{ active, item }">
                    <span
                        v-if="groupDescriptionIdForItem(item)"
                        :id="groupDescriptionIdForItem(item)"
                        class="sr-only">
                        {{ fieldsetLabelForItem(item) }}
                    </span>
                    <multiselect-blade-option
                        :is-single-select="isSingleSelectBlade"
                        :icon="item.icon || item.platform || item.kind"
                        :type="item.type"
                        :group-id="hasGroups ? item.groupId : 'blade-group'"
                        :item-id="item.id"
                        :label="item.name"
                        :training-attributes="item.trainingAttributes"
                        :application-flags="item.applicationFlags"
                        :sublabel="createOptionSublabel(item)"
                        :color="item.color"
                        :indeterminate="isOptionIndeterminate(item)"
                        :value="isOptionSelected(item)"
                        :disabled="isOptionDisabled(item)"
                        :disabled-tooltip="disabledTooltip"
                        :invisible="!active"
                        :collapsed="collapsedData.groups[item.id]"
                        :is-starred="item.isStarred"
                        :group-description-id="groupDescriptionIdForItem(item)"
                        @change="onOptionChange($event, item)"
                        @group-collapse="onGroupCollapse($event, item)"
                        @mouseenter="onOptionMouseenter($event, item)"
                        @mouseleave="$emit('mouseleave', $event)" />
                </template>
            </recycle-scroller>
            <slot name="popover" />
        </div>
        <template #footer>
            <div class="multiselect-blade__footer">
                <pendo-button
                    theme="app"
                    type="secondary"
                    :label="$t('Cancel')"
                    @click="onClose" />
                <pendo-button
                    v-if="!isSingleSelectBlade"
                    theme="app"
                    type="primary"
                    data-cy="next-button"
                    :label="actionText || $t('Apply')"
                    :disabled="applyDisabled"
                    @click="onApplyClick" />
            </div>
        </template>
    </pendo-drawer>
</template>

<script>
import { PendoButton, PendoDrawer, PendoLoadingIndicator, PendoTooltip } from '@pendo/components';
import { RecycleScroller } from '@/common/vue-virtual-scroller';
import lowerCase from 'lodash/lowerCase';
import debounce from 'lodash/debounce';
import useAriaLiveRegistry from '@/common/use/use-aria-live-registry/use-aria-live-registry';
import Search from '@/common/search/search.vue';
import MultiselectBladeOption from './multiselect-blade-option.vue';
import MultiselectBladeSelectedItem from './multiselect-blade-selected-item.vue';
import isEmpty from 'lodash/isEmpty';
import {
    addOptionMetadata,
    addGroupMetadata,
    addDivider,
    filterGroups,
    filterOptions,
    flattenOptions,
    getEmptyStateStrings,
    listDataToGroupIdLabel,
    sortData,
    sortByNumericProp,
} from './multiselect-blade.utils';
import { focusableLastRef } from '@/common/focusable-last-ref/focusable-last-ref.util';
import { useBreakpoints } from '@vueuse/core';
import { BREAKPOINTS } from '@pendo/less-variables/lib/utils/breakpoints';
import { pluralize } from '@/utils/formatters';
import { flatListToSections } from './multiselect-blade.utils';

export default {
    name: 'MultiselectBlade',
    components: {
        PendoButton,
        PendoDrawer,
        PendoLoadingIndicator,
        Search,
        MultiselectBladeOption,
        RecycleScroller,
        MultiselectBladeSelectedItem,
    },
    directives: {
        PendoTooltip,
    },
    provide () {
        return {
            $radioGroup: {
                $emit: () => {},
            },
        };
    },
    props: {
        appendTo: {
            type: [String, Element],
            default: undefined,
        },
        placement: {
            type: String,
            default: undefined,
        },
        positionStrategy: {
            type: String,
            default: undefined,
        },
        closeOnMaskClick: {
            type: Boolean,
            default: true,
        },
        value: {
            type: Object,
            default: () => ({}),
        },
        options: {
            type: Array,
            default: () => [],
        },
        coreEventOptions: {
            type: Array,
            default: () => [],
        },
        recentEventOptions: {
            type: Array,
            default: () => [],
        },
        filters: {
            type: Array,
            default: () => [],
        },
        beforeOptionsLabel: {
            type: String,
            default: '',
        },
        beforeOptions: {
            type: Array,
            default: () => [],
        },
        beforeOptionsTooltip: {
            type: String,
            default: '',
        },
        beforeOptionsSelected: {
            type: Boolean,
            default: false,
        },
        beforeOptionsDisabled: {
            type: Boolean,
            default: false,
        },
        selectedCountBuffer: {
            type: Number,
            default: 0,
        },
        max: {
            type: Number,
            default: Infinity,
        },
        title: {
            type: String,
            default: '',
        },
        visible: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: 'Option',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        actionText: {
            type: String,
            default: undefined,
        },
        applyDisabled: {
            type: Boolean,
            default: false,
        },
        optionSubLabelProperty: {
            type: String,
            default: 'appDisplayName',
        },
        sortByLastUpdated: {
            type: Boolean,
            default: false,
        },
        hasGroupSortKey: {
            type: Boolean,
            default: false,
        },
        isSingleSelectBlade: {
            type: Boolean,
            default: false,
        },
        showSelectAll: {
            type: Boolean,
            default: true,
        },
        selectionTypeLabel: {
            type: String,
            default: '',
        },
        disabledOptionId: {
            type: String,
            default: '',
        },
        disabledTooltip: {
            type: String,
            default: '',
        },
        showMask: {
            type: Boolean,
            default: true,
        },
        showCloseButton: {
            type: Boolean,
            default: true,
        },
    },
    emits: [
        'changeApplyDisabled',
        'showEmptyState',
        'apply',
        'close',
        'closed',
        'input',
        'open',
        'mouseenter',
        'mouseleave',
        'scroll',
    ],
    setup () {
        const breakpoints = useBreakpoints({
            large: BREAKPOINTS.LARGE,
        });
        const forceFullscreen = breakpoints.smaller('large');
        const { queueMessage } = useAriaLiveRegistry();

        return {
            forceFullscreen,
            queueMessage,
        };
    },
    data () {
        return {
            showFiltersAndSearch: true,
            search: '',
            collapsedData: { options: {}, groups: {}, isCoreEventsCollapsed: false, isRecentEventsCollapsed: false },
            platformOptionSelected: {},
            scrollTimer: null,
            isScrolling: false,
        };
    },
    computed: {
        hasGroups () {
            return this.options.some((option) => option.targets);
        },
        hasAnySelection () {
            return Object.values(this.allOptionsMap).some(({ id }) => this.value[id]);
        },
        hasAllSelected () {
            if (this.maxSelected) return true;

            return this.visibleOptions.every(({ id }) => this.value[id]);
        },
        visibleOptions () {
            const optionMap = new Map();

            // Build a mapping of option.id => option for every item being shown
            // with the current search string while ignoring collapse states.
            this.listData.forEach((opt) => {
                if (opt.targets) {
                    const targetOptions = addOptionMetadata(opt.targets, opt.id);
                    targetOptions.forEach((targetOption) => {
                        optionMap.set(targetOption.id, targetOption);
                    });
                }
                if (opt.type === 'option') {
                    optionMap.set(opt.id, opt);
                }
            });

            return Array.from(optionMap.values());
        },
        allOptionsMap () {
            const optionMap = {};

            this.options.forEach((opt) => {
                if (opt.targets) {
                    const targetOptions = addOptionMetadata(opt.targets, opt.id);
                    targetOptions.forEach((targetOption) => {
                        optionMap[targetOption.id] = targetOption;
                    });
                }
                if (opt.type === 'option') {
                    optionMap[opt.id] = opt;
                }

                if (!opt.type && !opt.targets) {
                    optionMap[opt.id] = opt;
                }
            });

            return optionMap;
        },
        topTenRecentsForDisplay () {
            return this.recentEventOptionsData.slice(0, 11);
        },
        topTenRecentTargetsForDisplay () {
            return this.recentEventOptionsData[0].targets.slice(0, 10);
        },
        listData () {
            let options;

            if (this.hasGroups) {
                options = filterGroups(this.options, this.search, this.filters).sort(sortData('section'));
                if (this.hasGroupSortKey) {
                    options = options.sort(sortData('sortKey'));
                }
                options.forEach((group) =>
                    group.targets.sort(
                        this.sortByLastUpdated ? sortByNumericProp('lastUpdatedAt', true) : sortData('name'),
                    ),
                );
                options = flattenOptions(options);
                options = options.filter((option) => !this.collapsedData.options[option.id]);
            } else {
                options = filterOptions(this.options, this.search, this.filters);
                options.sort(this.sortByLastUpdated ? sortByNumericProp('lastUpdatedAt', true) : sortData('name'));
                options = addOptionMetadata(options);
            }

            return options;
        },
        selectionCount () {
            return Object.values(this.value).filter(Boolean).length + this.selectedCountBuffer;
        },
        selectionCountLabel () {
            const count = new Intl.NumberFormat().format(this.selectionCount);
            const displayMax = this.max !== Infinity;
            const numberCountString = `<strong>${count}${displayMax ? ` of ${this.max}` : ''}</strong>`;
            if (this.selectionTypeLabel) {
                if (displayMax) {
                    return `${numberCountString} ${pluralize(this.selectionTypeLabel, this.max)} selected`;
                }

                return count === '1'
                    ? `${numberCountString} ${this.selectionTypeLabel} selected`
                    : `${numberCountString} ${pluralize(this.selectionTypeLabel, count)} selected`;
            }

            return `${numberCountString} selected`;
        },
        beforeOptionsData () {
            let events = [];
            if (this.beforeOptions.length) {
                if (this.beforeOptionsDisabled) {
                    this.beforeOptions.forEach((option) => {
                        option.disabled = true;
                    });
                }
                if (this.beforeOptionsSelected) {
                    this.beforeOptions.forEach((option) => {
                        option.checked = true;
                    });
                }
                const optionsGroup = {
                    id: 'options',
                    name: 'options',
                    section: this.beforeOptionsLabel,
                    color: null,
                    targets: [...this.beforeOptions],
                    checked: this.beforeOptionsSelected,
                    disabled: this.beforeOptionsDisabled,
                };

                [events] = filterGroups([optionsGroup], this.search, this.filters);
                if (events) {
                    events = addGroupMetadata(events, this.collapsedData.isCoreEventsCollapsed);
                    if (this.listData.length) {
                        events = addDivider(events);
                    }

                    return events;
                }
            }

            return [];
        },
        recentEventOptionsData () {
            const baseRecentOptions = {
                id: 'recentEvents',
                name: 'recentEvents',
                section: this.$t('multiselectBlade.Recently used in reporting'),
                color: null,
            };
            let recentEvents = [];
            if (this.recentEventOptions.length > 0) {
                const recentEventsGroup = {
                    ...baseRecentOptions,
                    targets: [...this.recentEventOptions],
                };

                [recentEvents] = filterGroups([recentEventsGroup], this.search, this.filters);
                if (recentEvents) {
                    recentEvents = addGroupMetadata(recentEvents, this.collapsedData.isRecentEventsCollapsed);
                    if (this.listData.length) {
                        recentEvents = addDivider(recentEvents);
                    }

                    return recentEvents;
                }
            } else if (
                this.listData.length &&
                (this.selectionTypeLabel === 'event' || this.selectionTypeLabel === 'guide')
            ) {
                const emptyRecents = {
                    ...baseRecentOptions,
                    targets: [{ type: 'empty', name: 'No recents to display. Recents will appear as usage occurs.' }],
                };
                const emptyRecentsGrouped = addGroupMetadata(emptyRecents, this.collapsedData.isRecentEventsCollapsed);

                return addDivider(emptyRecentsGrouped);
            }

            return [];
        },
        coreEventOptionsData () {
            let coreEvents = [];
            if (this.coreEventOptions.length > 0) {
                const coreEventsGroup = {
                    id: 'coreEvents',
                    name: 'coreEvents',
                    section: this.$t('Core events'),
                    color: null,
                    targets: [...this.coreEventOptions],
                };

                [coreEvents] = filterGroups([coreEventsGroup], this.search, this.filters);
                if (coreEvents) {
                    coreEvents = addGroupMetadata(coreEvents, this.collapsedData.isCoreEventsCollapsed);
                    if (this.listData.length) {
                        coreEvents = addDivider(coreEvents);
                    }

                    return coreEvents;
                }
            }

            return [];
        },
        finalBeforeOptionsData () {
            return [...this.beforeOptionsData, ...this.topTenRecentsForDisplay, ...this.coreEventOptionsData];
        },
        beforeSections () {
            return flatListToSections(this.finalBeforeOptionsData);
        },
        groupIdLabelMap () {
            if (!this.hasGroups || !this.listData.length) return {};

            return listDataToGroupIdLabel(this.listData);
        },
        bufferHeight () {
            const beforeOptionsHeight = this.finalBeforeOptionsData.reduce((sum, option) => sum + option.size, 0);

            // use default buffer pixels if core event section does not exceed value
            return beforeOptionsHeight > 200 ? beforeOptionsHeight : 200;
        },
        maxSelected () {
            return this.selectionCount >= this.max;
        },
        lowerCaseLabel () {
            if (this.label === 'any activity') {
                return 'apps';
            }

            return `${lowerCase(this.label)}s`;
        },
        searchPlaceholder () {
            return `Search ${this.lowerCaseLabel}...`;
        },
        noSearchResults () {
            return this.search.length && this.listData.length === 0 && this.finalBeforeOptionsData.length === 0;
        },
        showEmptyState () {
            const noData = !this.listData.length && !this.finalBeforeOptionsData.length;

            return noData || this.noSearchResults;
        },
        searchResultsCount () {
            const nonSelectableTypes = ['group', 'divider', 'empty'];
            const listDataCount = this.listData.filter((item) => !nonSelectableTypes.includes(item.type)).length;
            const beforeOptionsCount = this.finalBeforeOptionsData.filter(
                (item) => !nonSelectableTypes.includes(item.type),
            ).length;

            return listDataCount + beforeOptionsCount;
        },
        emptyStateStrings () {
            return getEmptyStateStrings(!this.options.length, this.noSearchResults, this.lowerCaseLabel);
        },
        selectedValue () {
            return Object.values(this.value).filter((val) => Object.hasOwnProperty.call(this.allOptionsMap, val.id));
        },
    },
    watch: {
        search (newValue) {
            if (newValue) {
                this.debouncedAnnounceSearchResults();
            } else {
                this.debouncedAnnounceSearchResults.cancel();
            }
        },
        showEmptyState (showEmptyState) {
            if (showEmptyState) {
                this.$emit('showEmptyState', true);
            }
        },
        options () {
            this.collapsedData = {
                options: {},
                groups: {},
                isCoreEventsCollapsed: false,
                isRecentEventsCollapsed: false,
            };
        },
        value: {
            handler () {
                // Keep the apply button disabled when all options are deselected
                if (Object.values(this.value).every((val) => val === false)) {
                    this.$emit('changeApplyDisabled', true);
                }
            },
            deep: true,
            immediate: true,
        },
    },
    created () {
        this.debouncedAnnounceSearchResults = debounce(this.announceSearchResults, 300);
    },
    beforeUnmount () {
        this.debouncedAnnounceSearchResults?.cancel();
    },
    methods: {
        getSectionRowKey (section, row, rowIndex) {
            return `${section.id}-${row.id || row.type}-${rowIndex}`;
        },
        fieldsetLabelForItem (item) {
            if (!this.hasGroups) {
                return this.$t('multiselectBlade.Select options');
            }

            return this.groupIdLabelMap[item.groupId] ?? item.name ?? item.groupId ?? '';
        },
        groupDescriptionIdForItem (item) {
            if (!this.hasGroups || item?.type !== 'option' || item.id == null) {
                return undefined;
            }

            return `multiselect-blade-option-desc-${item.id}`;
        },
        beforeSectionDescriptionId (section) {
            return section?.id != null ? `multiselect-blade-before-desc-${section.id}` : undefined;
        },
        announceSearchResults () {
            let message;
            if (this.showEmptyState) {
                message = [
                    this.emptyStateStrings.header,
                    this.emptyStateStrings.noSearchResultsMsg,
                    this.emptyStateStrings.suggestion,
                ]
                    .filter(Boolean)
                    .join(' ');
            } else {
                const count = this.searchResultsCount;
                message = this.$t(
                    'multiselectBlade.{count, plural, one {# result available} other {# results available}}',
                    { count },
                );
            }
            this.queueMessage({ message, ariaLiveMode: 'polite' });
        },
        onOptionMouseenter ($event, option) {
            if (this.isScrolling) {
                return;
            }

            const element = $event.currentTarget;
            this.$emit('mouseenter', { option, element });
        },
        sectionTooltip (option) {
            const isBeforeSection = option.type === 'group' && option.section === this.beforeOptionsLabel;
            if (isBeforeSection && this.beforeOptionsTooltip) {
                return this.beforeOptionsTooltip;
            }

            return '';
        },
        createOptionSublabel (item) {
            const isOptionWithSubLabel = item.type === 'option' && this.optionSubLabelProperty !== null;
            const subLabel = item[this.optionSubLabelProperty];
            if (isOptionWithSubLabel && subLabel !== undefined) return `${subLabel}`;

            return '';
        },
        onOptionChange ($event, option) {
            this.$emit('changeApplyDisabled', false);

            if (!this.isOptionDisabled(option)) {
                if (option.type === 'group') {
                    this.selectGroup(option);
                } else {
                    this.selectSingle(option, $event);
                }
            }
        },
        onApplyClick () {
            this.$emit('apply');
        },
        onClose () {
            this.$emit('close');
        },
        onClosed () {
            this.search = '';
            this.bladeOpen = false;
            this.$emit('closed');
            this.$emit('changeApplyDisabled', true);
        },
        getGroupStatus (group) {
            const groupTargets = group.id === 'recentEvents' ? this.topTenRecentTargetsForDisplay : group.targets;

            if (groupTargets.every(({ id }) => this.value[id])) {
                return { checked: true, indeterminate: false };
            }

            if (groupTargets.some(({ id }) => this.value[id])) {
                return { checked: false, indeterminate: true };
            }

            return { checked: false, indeterminate: false };
        },
        isOptionSelected (option) {
            const isCheckedGroup = option.type === 'group' && option.checked;
            const isCheckedOption = option.checked;
            if (isCheckedGroup || isCheckedOption) return true;

            if (option.type === 'group') {
                return this.getGroupStatus(option).checked;
            }

            return !!this.value[option.id];
        },
        isOptionDisabled (option) {
            const isDisabledGroup = option.type === 'group' && option.disabled;
            const isDisabledOption = option.disabled || (this.disabledOptionId && option.id === this.disabledOptionId);
            if (isDisabledGroup || isDisabledOption) return true;

            if (option.showCheckbox === false) return true;

            if (this.isSingleSelectBlade) return false;

            const basicCheck = !this.isOptionSelected(option) && this.maxSelected;

            return option.type === 'group' ? !this.getGroupStatus(option).indeterminate && basicCheck : basicCheck;
        },
        isOptionIndeterminate (option) {
            return option.type === 'group' && this.getGroupStatus(option).indeterminate;
        },
        clearSelections () {
            this.queueMessage({
                message: this.$t('multiselectBlade.Selections have been cleared successfully'),
                ariaLiveMode: 'polite',
            });
            this.$emit('changeApplyDisabled', false);
            this.$emit('input', {});
            this.focusBladeSelectionControls();
        },
        selectAll () {
            this.$emit('changeApplyDisabled', false);
            if (this.maxSelected) return;

            // Keep any selections that are currently hidden by the search filter.
            let newValue = {};

            Object.values(this.value).forEach((selection) => {
                newValue = Object.assign({}, { ...newValue, [selection.id]: selection });
            });

            let selectionsRemaining = this.max - this.selectionCount;

            // Add visible options based on selection criteria
            for (let i = 0; i < this.visibleOptions.length; i++) {
                const option = this.visibleOptions[i];

                if (!this.value[option.id]) {
                    newValue[option.id] = option;
                    selectionsRemaining--;
                }

                if (selectionsRemaining === 0) break;
            }

            this.$emit('input', newValue);
            this.focusBladeSelectionControls();
        },
        selectSingle (option, $event) {
            if (this.isSingleSelectBlade) {
                if ($event) {
                    this.$emit('input', Object.assign({}, { [option.id]: option }));

                    return;
                }
                // It's actually a deselect click.
                // However, some implementations do not gracefully handle emitting 'input' with an empty event. For now, this will be a no-op.
                // In a follow-up for a11y, the close-on-select behavior will be removed from all blades.

                return;
            }

            if (this.isOptionSelected(option)) {
                this.removeSelection(option);
            } else if (!this.maxSelected) {
                let { value } = this;
                if (!isEmpty(this.platformOptionSelected)) {
                    value = Object.assign({}, this.value, { [this.platformOptionSelected.id]: false });
                    this.platformOptionSelected = {};
                }
                this.$emit('input', Object.assign({}, value, { [option.id]: option }));
            }
        },
        selectGroup (group) {
            const allInGroupSelected = this.getGroupStatus(group).checked;
            if (allInGroupSelected || this.maxSelected) {
                if (group.targets.length > 1) {
                    this.removeGroupSelection(group);
                } else {
                    this.removeSelection(group.targets[0]);
                }
            } else {
                this.addGroupSelection(group);
            }
        },
        addGroupSelection (group) {
            const optionsToChange = {};
            let numSelectedItems = this.selectionCount;
            const groupTargets = group.id === 'recentEvents' ? this.topTenRecentTargetsForDisplay : group.targets;
            groupTargets.forEach((option) => {
                if (numSelectedItems >= this.max) return;
                if (!this.isOptionSelected(option)) {
                    optionsToChange[option.id] = option;
                    numSelectedItems++;
                }
            });

            this.$emit('input', Object.assign({}, this.value, optionsToChange));
        },
        removeSelection (optionToRemove) {
            const newValue = Object.assign({}, this.value, { [optionToRemove.id]: false });

            this.$emit('input', newValue);
        },
        removeGroupSelection (groupToRemove) {
            const idsToRemove = Object.fromEntries(groupToRemove.targets.map(({ id }) => [id, false]));
            const newValue = Object.assign({}, this.value, idsToRemove);

            this.$emit('input', newValue);
        },
        onGroupCollapse (collapsed, group) {
            this.collapsedData.groups[group.id] = collapsed;
            if (group.id === 'coreEvents') {
                this.collapsedData.isCoreEventsCollapsed = collapsed;
            } else if (group.id === 'recentEvents') {
                this.collapsedData.isRecentEventsCollapsed = collapsed;
            } else {
                group.targets.forEach((target) => {
                    this.$set(this.collapsedData.options, target.id, collapsed);
                });
            }
        },
        async focusBladeSelectionControls () {
            // The focus target may be disabled in the current DOM state.
            // Waiting two ticks ensures it is focusable in all conditions.
            await this.$nextTick();
            await this.$nextTick();
            focusableLastRef(this.$refs.multiselectBladeSelectionControls).focus();
        },
        onScroll () {
            this.$emit('scroll', true);
            this.isScrolling = true;
            if (this.scrollTimer !== null) {
                clearTimeout(this.scrollTimer);
            }

            this.scrollTimer = setTimeout(() => {
                this.$emit('scroll', false);
                this.isScrolling = false;

                const hoveredElem = document.querySelector('.multiselect-blade-option__row:hover');
                if (!hoveredElem) {
                    return;
                }

                const mouseenterEvent = new MouseEvent('mouseenter', {
                    view: window,
                    bubbles: true,
                    cancelable: true,
                });
                hoveredElem.dispatchEvent(mouseenterEvent);
            }, 300);
        },
    },
};
</script>

<style lang="less" scoped>
:deep(.multiselect-blade-selected-item) {
    .selected-item-description {
        .pendo-icon {
            position: relative;
            top: 2px;
        }
    }
}

.multiselect-blade {
    :deep(.pendo-drawer__header) {
        height: 58px;
    }

    :deep(.pendo-drawer__title) {
        font-size: 14px;
        font-weight: 600;
    }

    &__body {
        grid-area: body;
        display: grid;
        grid-template-rows: min-content minmax(0, 1fr);
        overflow: hidden;
        border-bottom: 1px solid var(--palette-gray-40);
        border-top: 1px solid var(--palette-gray-40);
    }

    &__options-list-controls {
        padding: 8px 24px 16px;
        border-bottom: 1px solid var(--palette-gray-40);
        min-width: 0;
    }

    &__extra-info {
        height: fit-content;
    }

    &__activity-type-filter {
        margin: 12px 0;
    }

    &__selection-filters {
        margin-top: 12px;
        display: grid;
        grid-auto-flow: column;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 9px;

        :deep(> div:last-of-type) {
            justify-self: flex-start;
        }
    }

    &__selection-controls--filters-visible {
        margin-top: 12px;
    }

    &__selection-controls {
        display: flex;
        align-items: center;

        .selection-count {
            flex-grow: 1;
        }

        .single-selection {
            display: flex;
            flex-direction: row;
            align-items: center;
            min-width: 0;
        }
    }

    &__loading-state {
        grid-area: body;
        display: grid;
        align-content: center;
        justify-content: center;
    }

    :deep(&__empty-state) {
        padding: 16px 24px;

        p {
            margin-bottom: 0;
        }

        .header {
            font-weight: 700;
        }
    }

    &__footer {
        display: flex;
        justify-content: right;
    }
}

:deep(.v-popover > .trigger) {
    width: 100%;
}

:deep(.pendo-checkbox + .pendo-checkbox) {
    margin-left: 0;
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.multiselect-blade {
    .pendo-drawer__content {
        grid-template-areas:
            'header header'
            'body body'
            'footer footer' !important;
        grid-template-rows: [header] minmax(58px, max-content) [body] auto [footer] minmax(62.5px, max-content) !important;
    }
}
</style>
